import React, { useState, useEffect, useRef } from 'react'

import axios from '@/utils/axios'

import AsyncSelect from 'react-select/async';
import { components } from 'react-select';

// Custom Menu component to show message
const Menu = props => {
  const { message, options } = props.selectProps;
  return (
    <components.Menu {...props}>
      <div>
        {props.children}
        {message && (
          <div style={{ padding: '8px', color: '#666', fontSize: '0.9em', borderTop: '1px solid #ddd', textAlign: 'center' }}>
            {message}
          </div>
        )}
      </div>
    </components.Menu>
  );
};

const FencerSelect = ({ name, fencerId, fencerValue, onChange, isClearable}) => {
  const [currentValue, setCurrentValue] = useState(!!fencerId ? {label: fencerValue, value: fencerId} : null);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if(onChange && !!currentValue?.data) onChange(currentValue.data)
    if(onChange && isClearable && !currentValue) onChange(null)
  }, [currentValue?.value])

  useEffect(() => {
    setCurrentValue( !!fencerId && !!fencerValue ? {label: fencerValue, value: fencerId} : null)
  }, [fencerId, fencerValue])

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const loadOptions = (inputValue, callback) => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (inputValue.length === 0) {
      callback([]);
      setMessage(null);
      setIsLoading(false);
      return;
    }

    // If length is less than 2, just show message without API call
    if (inputValue.length < 2) {
      callback([]);
      setMessage("Please type at least 2 characters to search");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Set a new timer for 1.5 seconds
    debounceTimerRef.current = setTimeout(() => {
      axios.get(`/fencers.json?search=${inputValue}`).then((res) => {
        // Check if response has the new structure
        if (res?.data?.fencers) {
          const fencers = res.data.fencers;
          const options = fencers.map(r => {
            let memb = []
            if(r.usfa_id) memb.push(`USFA#${r.usfa_id}`)
            if(r.cff_id) memb.push(`CFF#${r.cff_id}`)
            return {value: r.id, label: `${r.last_name}, ${r.first_name} (${r.primary_club_name}) [${memb.length > 0 ? memb.join(" ") : 'n/a'}]`, data: r}
          });
          
          // Display message from the backend
          setMessage(res.data.message || null);
          
          callback(options);
        } else {
          // Fallback for old API response format
          const options = res?.data?.map(r => {
            let memb = []
            if(r.usfa_id) memb.push(`USFA#${r.usfa_id}`)
            if(r.cff_id) memb.push(`CFF#${r.cff_id}`)
            return {value: r.id, label: `${r.last_name}, ${r.first_name} (${r.primary_club_name}) [${memb.length > 0 ? memb.join(" ") : 'n/a'}]`, data: r}
          });
          callback(options);
        }
        setIsLoading(false);
      }).catch(error => {
        console.error("Error fetching fencers:", error);
        callback([]);
        setMessage("Error fetching results. Please try again.");
        setIsLoading(false);
      });
    }, 1500); // 1.5 second debounce
  };

  // Custom styles to fix z-index issue
  const customStyles = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    menu: base => ({ ...base, zIndex: 9999 })
  };

  return (
    <AsyncSelect 
      cacheOptions
      isClearable={!!isClearable}
      loadOptions={loadOptions}
      defaultOptions
      styles={customStyles}
      value={currentValue}
      placeholder="Type to search, click to select"
      noOptionsMessage={({ inputValue }) => 
        inputValue.length < 2 
          ? "Type at least 2 characters to search" 
          : "No fencers found matching your search"
      }
      name={name}
      onChange={(v) => setCurrentValue(v)}
      message={message}
      components={{ Menu }}
      menuPortalTarget={document.body}
      isLoading={isLoading}
    />
  );
}

export default FencerSelect;